import { Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Role } from '@texas/api/endpoints/userApi';
import { useActiveSession } from '@texas/hooks/useSession';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { useNavigationStore } from '@texas/components/navigation/hooks/useNavigationStore';
import { configurationNavigationStyles } from '@texas/resources/theme/styles/configuration';

interface ConfigurationNavigationProps {
  organizationName: string;
}

interface ConfigurationItem {
  label: string;
  route: string;
}

const systemAdministratorItems: ConfigurationItem[] = [
  {
    label: 'configuration.navigation.articleTemplates',
    route: 'configuration.articleTemplates',
  },
  {
    label: 'configuration.navigation.articleTags',
    route: 'configuration.articleTags',
  },
  {
    label: 'configuration.navigation.branches',
    route: 'configuration.branches',
  },
  {
    label: 'configuration.navigation.branchRegions',
    route: 'configuration.branchRegions',
  },
  {
    label: 'configuration.navigation.countries',
    route: 'configuration.countries',
  },
  {
    label: 'configuration.navigation.currencies',
    route: 'configuration.currencies',
  },
  {
    label: 'configuration.navigation.matrixGroups',
    route: 'configuration.matrixGroups',
  },
  {
    label: 'configuration.navigation.matrixNodes',
    route: 'configuration.matrixNodes',
  },
  {
    label: 'configuration.navigation.deliveryMethod',
    route: 'configuration.deliveryMethods',
  },
  {
    label: 'configuration.navigation.deliveryTerms',
    route: 'configuration.deliveryTerms',
  },
  {
    label: 'configuration.navigation.paymentTerms',
    route: 'configuration.paymentTerms',
  },
  {
    label: 'configuration.navigation.freightCosts',
    route: 'configuration.freightCosts',
  },
  {
    label: 'configuration.navigation.handlingFees',
    route: 'configuration.handlingFees',
  },
  {
    label: 'configuration.navigation.materials',
    route: 'configuration.materials',
  },
  {
    label: 'configuration.navigation.productGroups',
    route: 'configuration.productGroups',
  },
  {
    label: 'configuration.navigation.complianceDocumentDefinitions',
    route: 'configuration.complianceDocumentDefinitions',
  },
  {
    label: 'configuration.navigation.techniques',
    route: 'configuration.techniques',
  },
  {
    label: 'configuration.navigation.vatCodes',
    route: 'configuration.vatCodes',
  },
  {
    label: 'configuration.navigation.treatments',
    route: 'configuration.treatments',
  },
  {
    label: 'configuration.navigation.dimensionGroups',
    route: 'configuration.dimensionGroups',
  },
  {
    label: 'configuration.navigation.vdcTemplates',
    route: 'configuration.vdcTemplates',
  },
  {
    label: 'configuration.navigation.vdcTiers',
    route: 'configuration.vdcTiers',
  },
  {
    label: 'configuration.navigation.internalApi',
    route: 'configuration.internalApi',
  },
  {
    label: 'configuration.navigation.qualities',
    route: 'configuration.qualities',
  },
  {
    label: 'configuration.navigation.organization',
    route: 'configuration.organization',
  },
  {
    label: 'configuration.navigation.garp',
    route: 'configuration.garp',
  },
];

const garpItem = systemAdministratorItems.find(
  (item) => item.route === 'configuration.garp',
)!;

function isStateActive(activeState: string, route: string) {
  return activeState === route || activeState.startsWith(`${route}.`);
}

export function ConfigurationNavigation({
  organizationName,
}: ConfigurationNavigationProps) {
  const { t } = useTranslation();
  const { activeState } = useNavigationStore();
  const { currentUser } = useActiveSession();
  const isSystemAdministrator = hasRole(currentUser.role, [
    Role.SystemAdministrator,
  ]);
  const items = isSystemAdministrator ? systemAdministratorItems : [garpItem];

  return (
    <Flex
      as="nav"
      aria-label={t('configuration.navigation.label')}
      sx={configurationNavigationStyles.root}
    >
      <Flex sx={configurationNavigationStyles.items}>
        {items.map((item) => {
          const active = isStateActive(activeState, item.route);
          const label =
            item.route === 'configuration.organization'
              ? organizationName
              : t(item.label as 'configuration.navigation.garp');

          return (
            <AngularNavLink
              key={item.route}
              to={item.route}
              navobj={{}}
              aria-current={active ? 'page' : undefined}
              variant={
                active
                  ? 'configurationNavigationActive'
                  : 'configurationNavigation'
              }
            >
              <Text noOfLines={1}>{label}</Text>
            </AngularNavLink>
          );
        })}
      </Flex>
    </Flex>
  );
}
